import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { usePagu, usePemakaianSaya } from '../../../domains/tenancy/api'
import { tanggal, uangUSD } from '../../../shared/lib/waktu'
import {
  Angka, Baris, Bendera, Body, Chip, Gagal, KepalaHalaman, Kosong, Memuat, Panel,
  Pilihan, Tabel,
} from '../../../shared/ui'

const RENTANG = [7, 30, 90]

/**
 * PEMAKAIAN — baca-saja: terpakai vs pagu.
 *
 * `DESAIN_BACKOFFICE.md` menaruhnya di kelompok "Perusahaan" dan menandainya baca-saja.
 * Pagunya ditetapkan KITA (tingkat kedua pola tiga tingkat), jadi tenant melihat berapa
 * yang terpakai — bukan mengubah batasnya sendiri.
 */

/**
 * PAGU BULAN INI — yang membuat layar ini bisa dipakai untuk bertindak, bukan cuma dibaca.
 *
 * Angkanya SUDAH menggerakkan sistem sejak ADR-064: `pagu_menahan()` menghentikan gambar
 * dan video lebih dulu, lalu drafting, dan chatbot paling akhir. Sampai hari ini tidak ada
 * satu pun layar yang memperlihatkannya — jadi klien berhenti dilayani tanpa peringatan,
 * dan yang pertama tahu orang yang menekan tombol lalu gagal.
 *
 * TANPA PAGU BUKAN NOL. Tenant yang belum diberi pagu tidak dibatasi apa pun; menampilkan
 * "0% terpakai" untuknya adalah kabar baik yang salah. Karena itu backend mengirim `null`
 * dan panel ini mengatakannya apa adanya.
 */
function Pagu({ t, locale }) {
  const q = usePagu()
  if (q.isLoading) return <Memuat tinggi={3} />
  // Gagal memuat pagu TIDAK boleh menjatuhkan seluruh layar pemakaian — angka pemakaian
  // di bawahnya tetap berguna sendirian.
  if (q.isError || !q.data) return null

  const d = q.data
  const adaPagu = d.pagu_bulanan_usd != null
  const persen = d.persen ?? 0
  const nada = !adaPagu ? 'idle'
    : d.tahap !== 'aman' ? 'bad'
    : persen >= d.ambang_peringatan * 100 ? 'warn' : 'ok'

  return (
    <Panel judul={t('pk.bulanIni')} catatan={t('pk.hariTersisa', { n: d.hari_tersisa })}>
      <Body className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[21px] tabular">
            {(d.panggilan_tanpa_tarif > 0 ? '≥ ' : '') + uangUSD(d.terpakai_usd, locale)}
          </b>
          {adaPagu && (
            <span className="text-[13px] text-ink2">
              {t('pk.dariPagu', { pagu: uangUSD(d.pagu_bulanan_usd, locale) })}
            </span>
          )}
          <span className="flex-1" />
          <Chip nada={nada}>
            {adaPagu ? t('pk.persenTerpakai', { n: persen }) : t('pk.tanpaPagu')}
          </Chip>
        </div>

        {adaPagu && (
          <div className="h-1.5 rounded bg-surface2 border border-line overflow-hidden">
            <div
              className={cxNada(nada)}
              /* Dibatasi 100% supaya bilah tidak meluber keluar kotaknya saat pagu
                 terlampaui — angka persennya di atas tetap menyebut nilai sebenarnya. */
              style={{ width: `${Math.min(persen, 100)}%`, height: '100%' }}
            />
          </div>
        )}

        <p className="m-0 text-[12px] text-ink2 max-w-[76ch]">
          {adaPagu
            ? t('pk.ambangCatatan', { n: Math.round(d.ambang_peringatan * 100) })
            : t('pk.tanpaPaguCatatan')}
        </p>

        {/* Yang berhenti SEKARANG, bukan penjelasan umum. Daftar ini datang dari backend
            dan mencerminkan tahap pagu saat ini — kalau kosong, belum ada yang berhenti. */}
        {d.dihentikan.length > 0 && (
          <Bendera nada="bad" role="alert">
            {t('pk.dihentikan', { daftar: d.dihentikan.join(' · ') })}
          </Bendera>
        )}

        <Bendera nada="info" judul={t('pk.urutanJudul')}>{t('pk.urutanIsi')}</Bendera>
      </Body>
    </Panel>
  )
}

// Warna bilah mengikuti nada chip-nya, supaya keduanya tidak pernah bercerita berbeda.
function cxNada(nada) {
  return { ok: 'bg-ok', warn: 'bg-warn', bad: 'bg-bad', idle: 'bg-ink3' }[nada] ?? 'bg-ink3'
}

export default function PemakaianSaya() {
  const { t, locale } = useBahasa()
  const [hari, setHari] = useState(30)
  const q = usePemakaianSaya(hari)

  const d = q.data

  return (
    <>
      <KepalaHalaman
        judul={t('ta.pemakaianJudul')}
        keterangan={t('ta.pemakaianKeterangan')}
        aksi={
          <Pilihan value={hari} onChange={(e) => setHari(Number(e.target.value))}>
            {RENTANG.map((n) => <option key={n} value={n}>{t('pk.hari', { n })}</option>)}
          </Pilihan>
        }
      />

      {q.isLoading && <Memuat tinggi={4} />}
      {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

      <Pagu t={t} locale={locale} />

      {d && (
        <>
          <div className="flex flex-wrap gap-2">
            <Angka label={t('pk.totalPanggilan')} nilai={d.total_count} locale={locale} />
            <Angka label={t('pk.totalToken')} nilai={d.total_tokens} locale={locale} />
            <Angka
              label={t('pk.totalBiaya')}
              nilai={(d.total_tanpa_tarif > 0 ? '≥ ' : '') + uangUSD(d.total_cost_usd, locale)}
              locale={locale}
            />
          </div>

          {d.total_tanpa_tarif > 0 && (
            <Bendera nada="warn" className="max-w-[76ch]">{t('ta.biayaBatasBawah', { n: d.total_tanpa_tarif })}</Bendera>
          )}

          {/* KE MANA PERGINYA — pemakaian per KEGIATAN, bukan per model.
              Orang yang membuka layar biaya menanyakan "uang saya habis untuk apa",
              dan model tidak menjawabnya: satu model yang sama menulis konsep DAN
              membalas chat, dengan biaya per panggilan yang jauh berbeda. */}
          <Panel judul={t('pk.keMana')} catatan={t('pk.keManaCatatan')}>
            {(d.per_kegiatan ?? []).length === 0 ? (
              <Kosong judul={t('pk.kosong')} />
            ) : (
              <Tabel kepala={[t('pk.kolKegiatan'), t('pk.kolJumlah'), t('pk.kolToken'),
                              t('pk.kolBiaya')]}>
                {d.per_kegiatan.map((k) => (
                  <Baris key={k.kegiatan}>
                    <td>
                      <span className="font-semibold">{k.label}</span>
                      <span className="block font-mono text-[10.5px] text-ink3">{k.kegiatan}</span>
                    </td>
                    <td className="tabular">{k.count}</td>
                    <td className="tabular">{k.tokens_used || '—'}</td>
                    <td className="tabular">
                      {uangUSD(k.cost_estimate_usd)}
                      {k.panggilan_tanpa_tarif > 0 && (
                        <span className="block text-[10.5px] text-warn">
                          {t('pk.tanpaTarif', { n: k.panggilan_tanpa_tarif })}
                        </span>
                      )}
                    </td>
                  </Baris>
                ))}
              </Tabel>
            )}
          </Panel>

          <Panel judul={t('pk.perHari')}>
            {d.days.length === 0 ? (
              <Kosong judul={t('pk.kosong')} />
            ) : (
              <Tabel kepala={[t('pk.kolTanggal'), t('pk.kolJenis'), t('pk.kolJumlah'),
                              t('pk.kolToken'), t('pk.kolBiaya')]}>
                {d.days.map((r, i) => (
                  <Baris key={`${r.date}-${r.label || r.resource_type}-${i}`}>
                    <td className="font-mono text-[11.5px] whitespace-nowrap">
                      {tanggal(r.date, locale)}
                    </td>
                    <td className="font-mono text-[11.5px] text-ink2">{r.label || r.resource_type}</td>
                    <td className="tabular">{r.count}</td>
                    <td className="tabular">{r.tokens_used}</td>
                    <td className="tabular">
                      {r.panggilan_tanpa_tarif > 0 ? '≥ ' : ''}
                      {uangUSD(r.cost_estimate_usd, locale)}
                    </td>
                  </Baris>
                ))}
              </Tabel>
            )}
          </Panel>
        </>
      )}
    </>
  )
}
